import { getSupabase } from '../../lib/supabase';
import type { NestedFormSource } from './form-block-definition';

/** A picker session captures the workspace and the original insertion point. */
export interface NestedFormSelection {
  orgId: string;
  workspaceId: string;
  campaignId?: string;
  onSelect: (source: NestedFormSource) => void;
}

/** The list never needs a form's schema or respondent data. */
export interface NestedFormSummary {
  id: string;
  target_name: string;
  question_count: number;
  room_id: string | null;
}

const PAGE_SIZE = 12;

/** The database searches the full visible workspace and returns one lookahead row. */
export async function searchNestedFormSources({ orgId, workspaceId, campaignId, search, offset, signal }: Omit<NestedFormSelection, 'onSelect'> & {
  search: string; offset: number; signal: AbortSignal;
}): Promise<{ rows: NestedFormSummary[]; hasMore: boolean }> {
  const { data, error } = await getSupabase().rpc('search_nested_form_sources', {
    p_org_id: orgId, p_workspace_id: workspaceId, p_campaign_id: campaignId ?? null,
    p_search: search.trim(), p_offset: offset,
  }).abortSignal(signal);
  if (error) throw error;
  const rows: NestedFormSummary[] = data ?? [];
  return { rows: rows.slice(0, PAGE_SIZE), hasMore: rows.length > PAGE_SIZE };
}

/** Recheck workspace access and availability when the author selects a result. */
export async function loadNestedFormSource(orgId: string, workspaceId: string, id: string, signal: AbortSignal): Promise<NestedFormSource> {
  const { data, error } = await getSupabase().from('interview_sessions').select('id,target_name,form_schema,conversation_config,require_video,require_name,require_email,identity_collection')
    .eq('organization_id', orgId).eq('brand_id', workspaceId).eq('status', 'active').eq('id', id).abortSignal(signal).single();
  if (error) throw error;
  return { ...data, target_name: data.target_name?.trim() || 'Untitled' };
}
